import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaCheck } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import { aprovarSolicitacaoEnvio, recusarSolicitacaoEnvio } from "../utils/solicitacoesEnvio";
import { buscarMinhasConfirmacoes, marcarCiente, desmarcarCiente } from "../utils/ciencia";
import "./NotificationsPanel.css";

function formatarDataRelativa(dataString) {
  if (!dataString) return "";
  const data = new Date(dataString);
  const diffMs = Date.now() - data.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return "Agora mesmo";
  if (diffMin < 60) return `${diffMin} min atrás`;
  if (diffH < 24) return `${diffH}h atrás`;
  if (diffD === 1) return "Ontem";
  if (diffD < 7) return `${diffD} dias atrás`;
  return data.toLocaleDateString("pt-BR");
}

export default function NotificationsPanel({ user, containerAtual, setContainerAtual, onClose, onCountChange }) {
  const navigate = useNavigate();
  const [convites, setConvites] = useState([]);
  const [mencoes, setMencoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processandoSolicitacao, setProcessandoSolicitacao] = useState(null);
  const [minhasConfirmacoes, setMinhasConfirmacoes] = useState(new Set());
  const [processandoCiencia, setProcessandoCiencia] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!user?.id || !user?.email) {
      setConvites([]);
      setMencoes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: convitesData } = await supabase
        .from("convites")
        .select("*")
        .eq("email", user.email)
        .in("status", ["pendente", "aceito"]);

      let convitesFormatados = [];
      if (convitesData?.length) {
        convitesFormatados = await Promise.all(
          convitesData.map(async (c) => {
            const { data: remetente } = await supabase
              .from("profiles")
              .select("id, nome, avatar_url")
              .eq("id", c.remetente_id)
              .maybeSingle();
            return { ...c, remetente, _tipo: "convite" };
          })
        );
      }

      const { data: notifData } = await supabase
        .from("notificacoes")
        .select(`
          id, user_id, remetente_id, nota_id, pilha_id, container_id, projeto_id, setor_id,
          mensagem, lido, tipo, tipo_nota, created_at, solicitacao_envio_id,
          remetente:profiles!notificacoes_remetente_id_fkey(id, nome, avatar_url),
          nota:notas(id, nome, tipo)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false }); // tiebreak para inserts em lote com o mesmo timestamp

      let mencoesFormatadas = [];
      if (notifData?.length) {
        // =============================================
        // Separa IDs de projetos e setores separadamente
        // =============================================
        const pilhaIds = [...new Set(notifData.map((n) => n.pilha_id).filter(Boolean))];
        const containerIds = [...new Set(notifData.map((n) => n.container_id).filter(Boolean))];
        const projetoIds = [...new Set(notifData.map((n) => n.projeto_id).filter(Boolean))];
        const setorIds = [...new Set(notifData.map((n) => n.setor_id).filter(Boolean))];

        // =============================================
        // Busca todos os dados em paralelo
        // =============================================
        const [pilhasRes, containersRes, projetosRes, setoresRes] = await Promise.all([
          pilhaIds.length ? supabase.from("pilhas").select("id, title").in("id", pilhaIds) : Promise.resolve({ data: [] }),
          containerIds.length ? supabase.from("profiles").select("id, nickname, nome").in("id", containerIds) : Promise.resolve({ data: [] }),
          projetoIds.length ? supabase.from("projects").select("id, name").in("id", projetoIds) : Promise.resolve({ data: [] }),
          setorIds.length ? supabase.from("setores").select("id, name").in("id", setorIds) : Promise.resolve({ data: [] }),
        ]);

        // =============================================
        // Cria os Maps para lookup rápido
        // =============================================
        const pilhaMap = new Map(pilhasRes.data?.map((p) => [p.id, p]) || []);
        const containerMap = new Map(containersRes.data?.map((c) => [c.id, c]) || []);
        
        // =============================================
        // unidadeMap combina projetos E setores
        // =============================================
        const unidadeMap = new Map([
          ...(projetosRes.data?.map((u) => [u.id, u]) || []),
          ...(setoresRes.data?.map((u) => [u.id, u]) || []),
        ]);

        mencoesFormatadas = notifData.map((n) => {
          const container = containerMap.get(n.container_id);
          const pilha = pilhaMap.get(n.pilha_id);
          // =============================================
          // Busca no unidadeMap por projeto_id OU setor_id
          // =============================================
          const unidade = unidadeMap.get(n.projeto_id) || unidadeMap.get(n.setor_id);
          const containerNome = container?.nickname || container?.nome || "Container";
          const isCalendario = n.tipo_nota === "Calendário" || n.tipo === "evento_calendario";
          const caminho = isCalendario
            ? [containerNome, unidade?.name || "Unidade", n.nota?.nome || "Calendário"].join(" / ")
            : [containerNome, unidade?.name || "Unidade", pilha?.title || "Pilha", n.nota?.nome || "Tarefa"].join(" / ");
          return { ...n, _tipo: "mencao", caminho, isCalendario };
        });
      }

      setConvites(convitesFormatados);
      setMencoes(mencoesFormatadas);

      // Busca quais nota_ids o usuário já confirmou ciência
      const notaIdsUnicos = [...new Set(mencoesFormatadas.map((m) => m.nota_id).filter(Boolean))];
      const confirmadas = await buscarMinhasConfirmacoes(user.id, notaIdsUnicos);
      setMinhasConfirmacoes(confirmadas);

      const unread =
        convitesFormatados.filter((c) => c.status === "pendente").length +
        mencoesFormatadas.filter((m) => !m.lido).length;
      onCountChange?.(unread);
    } catch (err) {
      console.error("Erro ao buscar notificações:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email, onCountChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Atualiza a lista em tempo real quando chega notificação nova ──
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`notificacoes-panel-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notificacoes", filter: `user_id=eq.${user.id}` },
        () => fetchAll()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchAll]);

  // marca menções como lidas e remove da lista ao abrir o painel
  useEffect(() => {
    if (loading) return;
    const naoLidas = mencoes.filter((m) => !m.lido && m.tipo !== "solicitacao_envio_nota");
    if (!naoLidas.length) return;
    const ids = naoLidas.map((m) => m.id);

    const timer = setTimeout(() => {
      supabase.from("notificacoes").update({ lido: true }).in("id", ids).then(() => {
        setMencoes((prev) => prev.filter((m) => !ids.includes(m.id)));
        onCountChange?.(convites.filter((c) => c.status === "pendente").length);
      });
    }, 1200);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const aceitarConvite = async (convite) => {
    try {
      await supabase.from("convites").update({ status: "aceito" }).eq("id", convite.id);
      fetchAll();
    } catch (err) {
      console.error("Erro ao aceitar convite:", err);
    }
  };

  const excluirMencao = async (id) => {
    try {
      await supabase.from("notificacoes").delete().eq("id", id);
      setMencoes((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Erro ao excluir notificação:", err);
    }
  };

  const toggleCiente = async (item) => {
    if (!item.nota_id || !user?.id) return;
    setProcessandoCiencia(item.nota_id);
    const jaConfirmado = minhasConfirmacoes.has(item.nota_id);
    const ok = jaConfirmado
      ? await desmarcarCiente(user.id, item.nota_id)
      : await marcarCiente(user.id, item.nota_id);
    if (ok) {
      setMinhasConfirmacoes((prev) => {
        const novo = new Set(prev);
        jaConfirmado ? novo.delete(item.nota_id) : novo.add(item.nota_id);
        return novo;
      });
    }
    setProcessandoCiencia(null);
  };

  const aceitarSolicitacaoEnvio = async (item) => {
    setProcessandoSolicitacao(item.solicitacao_envio_id);
    try {
      await aprovarSolicitacaoEnvio(item.solicitacao_envio_id);
      // Não remove a notificação - ela será atualizada para "solicitacao_envio_resolvida"
      await fetchAll();
    } catch (err) {
      console.error("Erro ao aprovar solicitação:", err);
      alert("Erro ao aceitar a solicitação. Tente novamente.");
    } finally {
      setProcessandoSolicitacao(null);
    }
  };

  const recusarSolicitacao = async (item) => {
    setProcessandoSolicitacao(item.solicitacao_envio_id);
    try {
      await recusarSolicitacaoEnvio(item.solicitacao_envio_id);
      // Não remove a notificação - ela será atualizada para "solicitacao_envio_resolvida"
      await fetchAll();
    } catch (err) {
      console.error("Erro ao recusar solicitação:", err);
      alert("Erro ao recusar a solicitação. Tente novamente.");
    } finally {
      setProcessandoSolicitacao(null);
    }
  };

  const navegarParaCaminho = (n, nivel) => {
    onClose();
    if (n.container_id && n.container_id !== containerAtual) {
      setContainerAtual(n.container_id);
    }
    if (nivel === 0) {
      navigate(`/containers/${n.container_id}`);
      return;
    }
    const entityId = n.projeto_id || n.setor_id;
    if (!entityId) return;
    const params = new URLSearchParams({
      entityId,
      type: n.setor_id ? "setor" : "project",
      containerId: n.container_id,
    });
    const abrirNota = nivel >= 3 || n.isCalendario;
    if (abrirNota) params.set("nota", n.nota_id);
    navigate(`/cards/${entityId}?${params.toString()}`, {
      state: {
        projectId: n.projeto_id,
        setorId: n.setor_id,
        pilhaId: nivel >= 2 ? n.pilha_id : undefined,
        notaId: abrirNota ? n.nota_id : undefined,
        entityType: n.setor_id ? "setor" : "project",
        containerId: n.container_id,
        openNota: abrirNota,
      },
    });
  };

  const itens = [...convites, ...mencoes].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  return (
    <div className="cx-notif-panel">
      <div className="cx-notif-panel__header">
        <span>Notificações</span>
      </div>

      <div className="cx-notif-panel__list">
        {loading ? (
          <p className="cx-notif-panel__empty">Carregando...</p>
        ) : itens.length === 0 ? (
          <p className="cx-notif-panel__empty">Nenhuma notificação por aqui.</p>
        ) : (
          itens.map((item) => {
            if (item._tipo === "convite") {
              return (
                <div key={`c-${item.id}`} className="cx-notif-item">
                  <div className="cx-notif-item__avatar">
                    {item.remetente?.avatar_url ? (
                      <img src={item.remetente.avatar_url} alt={item.remetente?.nome} />
                    ) : (
                      item.remetente?.nome?.charAt(0)?.toUpperCase() || "?"
                    )}
                  </div>
                  <div className="cx-notif-item__body">
                    <span className="cx-notif-item__text">
                      <strong>{item.remetente?.nome || "Alguém"}</strong> te convidou para colaborar
                      {item.status === "aceito" && <em> · aceito</em>}
                    </span>
                    <span className="cx-notif-item__time">{formatarDataRelativa(item.created_at)}</span>
                  </div>
                  {item.status === "pendente" && (
                    <button className="cx-notif-item__accept" onClick={() => aceitarConvite(item)} title="Aceitar convite">
                      <FaCheck size={11} />
                    </button>
                  )}
                </div>
              );
            }

            const partes = item.caminho ? item.caminho.split(" / ") : [];
            const isSolicitacaoPendente = item.tipo === "solicitacao_envio_nota";
            const processando = processandoSolicitacao === item.solicitacao_envio_id;

            return (
              <div key={`m-${item.id}`} className="cx-notif-item">
                <div className="cx-notif-item__avatar">
                  {item.remetente?.avatar_url ? (
                    <img src={item.remetente.avatar_url} alt={item.remetente?.nome} />
                  ) : (
                    item.remetente?.nome?.charAt(0)?.toUpperCase() || "?"
                  )}
                </div>
                <div className="cx-notif-item__body">
                  <span className="cx-notif-item__text">{item.mensagem || "Você foi mencionado em uma tarefa."}</span>
                  <div className="cx-notif-item__caminho">
                    {partes.map((parte, i) => (
                      <React.Fragment key={i}>
                        <span onClick={() => navegarParaCaminho(item, i)}>{parte}</span>
                        {i < partes.length - 1 && <span className="cx-notif-item__sep"> / </span>}
                      </React.Fragment>
                    ))}
                  </div>
                  {!isSolicitacaoPendente && item.nota_id && (
                    <label className="cx-notif-item__ciente">
                      <input
                        type="checkbox"
                        checked={minhasConfirmacoes.has(item.nota_id)}
                        disabled={processandoCiencia === item.nota_id}
                        onChange={() => toggleCiente(item)}
                      />
                      <span>Visto</span>
                    </label>
                  )}
                  {isSolicitacaoPendente && (
                    <div className="cx-notif-item__acoes-solicitacao">
                      <button
                        className="cx-notif-item__aceitar"
                        disabled={processando}
                        onClick={() => aceitarSolicitacaoEnvio(item)}
                      >
                        {processando ? "Processando..." : "Aceitar"}
                      </button>
                      <button
                        className="cx-notif-item__recusar"
                        disabled={processando}
                        onClick={() => recusarSolicitacao(item)}
                      >
                        Recusar
                      </button>
                    </div>
                  )}
                  <span className="cx-notif-item__time">{formatarDataRelativa(item.created_at)}</span>
                </div>
                {!isSolicitacaoPendente && (
                  <button className="cx-notif-item__delete" onClick={() => excluirMencao(item.id)} title="Excluir">
                    <FaTrash size={11} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}